import apiClient from './client';
import type {
  TaskPhaseWithCount,
  TaskPhaseRecord,
  CreateTaskPhaseRequest,
  UpdateTaskPhaseRequest,
} from '../../types';

/**
 * API for managing task phases (admin only)
 */
export const taskPhasesApi = {
  /**
   * Get all task phases with template counts
   */
  getAll: async (includeArchived?: boolean): Promise<TaskPhaseWithCount[]> => {
    const params = new URLSearchParams();
    if (includeArchived) params.set('include_archived', 'true');
    const queryString = params.toString();
    return apiClient.get<TaskPhaseWithCount[]>(
      `/api/admin/phases${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get a single phase by ID
   */
  getById: async (id: string): Promise<TaskPhaseWithCount> => {
    return apiClient.get<TaskPhaseWithCount>(`/api/admin/phases/${id}`);
  },

  /**
   * Create a new task phase
   */
  create: async (data: CreateTaskPhaseRequest): Promise<TaskPhaseRecord> => {
    return apiClient.post<TaskPhaseRecord>('/api/admin/phases', data);
  },

  /**
   * Update a task phase
   */
  update: async (id: string, data: UpdateTaskPhaseRequest): Promise<TaskPhaseRecord> => {
    return apiClient.put<TaskPhaseRecord>(`/api/admin/phases/${id}`, data);
  },

  /**
   * Archive a task phase
   */
  archive: async (id: string): Promise<{ archived: boolean }> => {
    return apiClient.post<{ archived: boolean }>(`/api/admin/phases/${id}/archive`);
  },

  /**
   * Restore an archived task phase
   */
  restore: async (id: string): Promise<{ restored: boolean }> => {
    return apiClient.post<{ restored: boolean }>(`/api/admin/phases/${id}/restore`);
  },

  /**
   * Reorder phases
   */
  reorder: async (phases: { id: string; sort_order: number }[]): Promise<TaskPhaseWithCount[]> => {
    return apiClient.put<TaskPhaseWithCount[]>('/api/admin/phases/reorder', { phases });
  },

  /**
   * Delete a task phase
   */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/api/admin/phases/${id}`);
  },
};

export default taskPhasesApi;
